// CLI BL567 — `pollPlugTask` service.
//
// When `QUEUED_PLUG_ENABLED` is on AND an entity (or its org) is allowlisted,
// `POST /api/v2/entities/:id/plug` enqueues the deploy on the BL566 task
// substrate and answers 202 with an additional `task_id` (and `queued: true`)
// alongside the usual `trace_id`. The deploy then runs on the lease-based
// sweeper with idempotent checkpoints, so a pod restart RESUMES rather than
// orphaning the deploy.
//
// This service is the CLI consumer of `GET /api/v2/tasks/:id` — the read-side
// projection of a queued task. It polls every 2s, maps the task's execution
// state onto the SAME `MultiStepRenderer` step UX the in-process (trace) path
// uses, and returns the terminal outcome.
//
// ── What the task surface actually exposes (vs. the trace) ──
// `GET /api/v2/tasks/:id` projects the EXECUTION state: `status`,
// `current_step`, `completed_steps` (keys of the task's checkpoints),
// `attempts`, `result`, `error`. The registered `plug` task has exactly TWO
// steps — `build` and `deploy` (see api `plug-task-handler.ts`) — so the queued
// UX renders two rows: "Building" and "Deploying", plus the final "Live" block.
// The granular per-K8s-step trace (cloud_build / apply_manifests / wait_rollout
// / wait_settle / http_probe) is NOT carried on the task surface; it lives on
// the deploy EVENT (`/deploys/<trace_id>`), which the in-process path renders.
// Projecting sub-steps onto the task surface is an API change (out of scope).
//
// Failure model (mirrors `plug-poll.ts`):
// - terminal `failed` → throw `PlugTaskFailedError` carrying `last_error`.
// - poll budget (BL544 20-min default) elapsed → throw the shared
//   `PollTimeoutError` classified SOFT, so the CLI exits 2 with the same
//   "taking longer than expected, run grid status" guidance: the task is
//   durable and continues server-side past our wait budget.
// - transport blips → exponential backoff up to a 30s cumulative budget per
//   blip, then `PlugTaskNetworkLostError`.
// - 404 → the server GC'd the task. If we'd already seen a successful poll the
//   task completed and was cleaned up (graceful exit); otherwise it's a real
//   "no such task" (stale id / wrong org).

import type { IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import type { Step } from '../render/multi-step.js';
import { CloudGridError } from '../utils.js';
import type { TaskFailureReason } from '../../../shared/src/v1-types.js';
import { humanize } from '../../../shared/src/cli-vocab.js';
import {
  PollTimeoutError,
  DEFAULT_MAX_DURATION_MS,
  type DeployTrace,
} from './plug-poll.js';

// ─────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────

/** Lifecycle states a task can be in — mirrors the API's `TaskStatus`. */
export type TaskStatus =
  | 'pending'
  | 'running'
  | 'success'
  | 'failed'
  | 'interrupted';

/**
 * The read-side projection of a queued task, as returned by
 * `GET /api/v2/tasks/:id`. `completed_steps` is the KEY set of the task's
 * checkpoints (which includes build-internal handles like `build_ids`, not
 * just registered step names); `current_step` is already filtered to the
 * registered step list server-side.
 */
export interface TaskProjection {
  task_id: string;
  type: string;
  status: TaskStatus;
  current_step: string | null;
  completed_steps: string[];
  attempts: number;
  max_attempts: number;
  result: Record<string, unknown> | null;
  error: string | null;
  /**
   * BL591 — the structured failure reason (build / rollout / validation /
   * provision), or `null` until a classified failure occurs. The queued-poll
   * path reads this to render a human user-facing line per the BL580 vocabulary
   * and to carry `error_code` + `log_excerpt` into the cli_events telemetry the
   * BL593 top-level catch emits. `error` (above) remains the raw string.
   */
  error_message?: TaskFailureReason | null;
  /**
   * BL595 — present only while the plug substrate is in a maintenance window
   * (sweeper paused for a deploy) AND the task is still in flight. Carries the
   * friendly, server-supplied maintenance copy. The poll loop renders it once
   * and switches to a slow cadence instead of hammering the queue; it is NOT a
   * failure (the durable task resumes when the window closes).
   */
  maintenance?: { code: string; message: string } | null;
  created_at: string;
  updated_at: string;
}

/** Terminal outcome of {@link pollPlugTask}. Failures throw rather than return. */
export interface PlugTaskResult {
  /**
   * `'success'` — the task reached terminal success.
   * `'cleaned_up'` — the server GC'd a task we had already seen progress on, so
   * the deploy completed and its row was reaped before our final poll.
   */
  status: 'success' | 'cleaned_up';
  /** Final task projection; `null` on the `'cleaned_up'` path (the row is gone). */
  task: TaskProjection | null;
}

/**
 * The minimal renderer surface {@link pollPlugTask} drives, satisfied
 * structurally by `MultiStepRenderer`. Each tick passes the synthesized step
 * list (the same `Step[]` shape the in-process flow feeds the renderer).
 */
export interface PlugUxRenderer {
  update(steps: Step[]): void;
}

export interface PollPlugTaskOpts {
  /** Poll cadence between successful polls. Default 2000ms. */
  intervalMs?: number;
  /** Hard poll budget. Default 20 minutes (BL544 `DEFAULT_MAX_DURATION_MS`). */
  timeoutMs?: number;
  /** Entity slug — used only to word the soft-timeout guidance. */
  slug?: string;
  /** Trace id — used only to word the soft-timeout guidance. */
  traceId?: string;
  /** Sleep adapter for tests. Defaults to a setTimeout-backed delay. */
  sleep?: (ms: number) => Promise<void>;
  /** Wall clock for the timeout budget. Defaults to `Date.now`. */
  now?: () => number;
  /**
   * BL595 — invoked ONCE, the first poll on which the task carries a
   * `PLUG_MAINTENANCE` maintenance block, so the caller can print a friendly
   * one-line update (not an error). Absent in `--json` mode (no chrome). The
   * cadence switch to {@link MAINTENANCE_POLL_INTERVAL_MS} happens regardless of
   * whether this is provided.
   */
  onMaintenance?: (info: { code: string; message: string }) => void;
}

// ─────────────────────────────────────────────────────────────────────────
// Errors
// ─────────────────────────────────────────────────────────────────────────

/** Terminal `failed` on the task. Carries the human failure line as the message. */
export class PlugTaskFailedError extends CloudGridError {
  /**
   * BL591 — the structured failure reason projected from the task's
   * `error_message`, attached so the BL593 top-level catch can forward
   * `error_code` + `log_excerpt` into the cli_events row. Absent on a legacy
   * (pre-BL591) task whose `error_message` was null.
   */
  public readonly failureReason?: TaskFailureReason;
  constructor(
    public readonly taskId: string,
    message: string,
    failureReason?: TaskFailureReason,
  ) {
    super(message);
    this.name = 'PlugTaskFailedError';
    if (failureReason) this.failureReason = failureReason;
    // BL593 — carry the task id so the top-level catch's cli_events row
    // records which queued plug task failed (`metadata.task_id`). Set at the
    // single construction site so both the --json and human queued paths
    // inherit it. BL591 — when the task carried a structured reason, also surface
    // `error_code` (the failure kind) + a `log_excerpt` so the unchanged BL593
    // emit (`metadata` passthrough in handle-error.ts) records WHICH failure mode
    // it was, not just that a task failed.
    (this as { _telemetryMetadata?: Record<string, unknown> })._telemetryMetadata = {
      task_id: taskId,
      ...(failureReason
        ? {
            error_code: failureReason.kind,
            ...(failureExcerpt(failureReason)
              ? { log_excerpt: failureExcerpt(failureReason) }
              : {}),
          }
        : {}),
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────
// BL591 — human failure rendering (BL580 vocabulary)
// ─────────────────────────────────────────────────────────────────────────

/** Max chars of build-log tail / evidence shown to the user under the headline. */
const FAILURE_DETAIL_MAX_CHARS = 800;
/** Max chars of the excerpt carried into cli_events telemetry. */
const FAILURE_EXCERPT_MAX_CHARS = 1000;

/**
 * BL580 + BL591 — the full user-facing failure block, rendered from the shared
 * humanize() catalogue (`packages/shared/src/cli-vocab.ts`): a plain headline
 * and one-sentence expansion (`<title>: <detail>`), then the user's own
 * scrubbed build/runtime tail (when present, truncated), then the one concrete
 * next step. The catalogue is the single source of the §23-voiced copy — the
 * task layer only assembles the lines and truncates the tail. Used as the
 * `PlugTaskFailedError` message so the top-level `Error: …` print shows it all.
 */
export function formatTaskFailure(reason: TaskFailureReason): string {
  const h = humanize(reason);
  const lines = [`${h.title}: ${h.detail}`];
  if (h.log_tail && h.log_tail.length > 0) {
    lines.push(
      h.log_tail.length > FAILURE_DETAIL_MAX_CHARS
        ? `${h.log_tail.slice(0, FAILURE_DETAIL_MAX_CHARS)}…`
        : h.log_tail,
    );
  }
  lines.push(h.next_step);
  return lines.join('\n');
}

/**
 * dino-store (2026-07-16) — the machine-parseable failure object emitted in
 * queued-plug `--json` mode so an AGENT driving the CLI can fix + replug without
 * scraping stderr. Requirement C: legible, structured, with the offending thing
 * named and the fix stated.
 *
 * Fields:
 *   - `code`        — a stable machine code. For a build failure with a matched
 *                     classifier (e.g. a pinned dependency that doesn't exist)
 *                     this is `BUILD_DEP_NOT_FOUND` / `BUILD_FRAMEWORK_INCOMPAT`;
 *                     otherwise it is the failure `kind` upper-cased
 *                     (`BUILD_FAILED`, `ROLLOUT_FAILED`, …).
 *   - `kind`        — the raw `TaskFailureReason` kind.
 *   - `reason`      — a plain one-sentence "what went wrong" (§23 `detail`).
 *   - `remediation` — the single concrete next step (§23 `next_step`).
 *   - `service` / `build_id` / `package` / `version` / `log_tail` — present when
 *     known, so an agent can act without re-parsing prose.
 */
export interface QueuedPlugJsonError {
  code: string;
  kind: TaskFailureReason['kind'];
  reason: string;
  remediation: string;
  service?: string;
  build_id?: string;
  package?: string;
  version?: string;
  log_tail?: string;
}

/**
 * dino-store — build the {@link QueuedPlugJsonError} an agent parses from the
 * task's structured failure reason. Pure + exported for unit testing.
 */
export function taskFailureToJson(reason: TaskFailureReason): QueuedPlugJsonError {
  const h = humanize(reason);
  const classification =
    reason.kind === 'build_failed' ? reason.classification : undefined;
  const code =
    classification?.code ?? reason.kind.toUpperCase();
  const out: QueuedPlugJsonError = {
    code,
    kind: reason.kind,
    reason: h.detail,
    remediation: h.next_step,
  };
  if (reason.kind === 'build_failed') {
    if (reason.service) out.service = reason.service;
    if (reason.build_id) out.build_id = reason.build_id;
    if (classification?.package) out.package = classification.package;
    if (classification?.version) out.version = classification.version;
  }
  if (h.log_tail && h.log_tail.length > 0) {
    out.log_tail =
      h.log_tail.length > FAILURE_EXCERPT_MAX_CHARS
        ? `${h.log_tail.slice(0, FAILURE_EXCERPT_MAX_CHARS)}…`
        : h.log_tail;
  }
  return out;
}

/**
 * BL591 — the excerpt carried into the cli_events `log_excerpt` field: the build
 * log tail / rollout evidence / the message, truncated. Distinct from the
 * user-facing detail so telemetry keeps a reason even for validation/provision
 * failures (which have no separate tail).
 */
function failureExcerpt(reason: TaskFailureReason): string | null {
  const raw =
    reason.kind === 'build_failed'
      ? reason.log_tail ?? reason.message
      : reason.kind === 'rollout_failed'
        ? reason.evidence ?? reason.message
        : reason.message;
  if (!raw || raw.length === 0) return null;
  return raw.length > FAILURE_EXCERPT_MAX_CHARS ? `${raw.slice(0, FAILURE_EXCERPT_MAX_CHARS)}…` : raw;
}

/** Cumulative transport-blip backoff budget exhausted. */
export class PlugTaskNetworkLostError extends CloudGridError {
  constructor(
    public readonly taskId: string,
    public readonly attempts: number,
  ) {
    super(
      `Lost contact with the API while polling task ${taskId} (${attempts} consecutive failures).`,
    );
    this.name = 'PlugTaskNetworkLostError';
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Step mapping
// ─────────────────────────────────────────────────────────────────────────

const DEFAULT_POLL_INTERVAL_MS = 2000;
// BL595 — the slow cadence used while the substrate is in a maintenance window.
// The durable task won't drain until the deploy completes, so polling every 2s
// only burns API calls; 30s is responsive enough to catch the resume.
const MAINTENANCE_POLL_INTERVAL_MS = 30_000;
// Per-blip transport backoff: double each consecutive failure up to a cap,
// abandoning once the cumulative intended-sleep for a single blip run exceeds
// the budget. Tracked as intended-sleep (not wall clock) so the budget is
// deterministic under an injected instant `sleep` in tests.
const NETWORK_BUDGET_MS = 30_000;
const BACKOFF_BASE_MS = 500;
const BACKOFF_MAX_MS = 8000;

// The registered `plug` task steps, in execution order. The task surface may
// also report build-internal checkpoint keys (e.g. `build_ids`); those are not
// steps and are ignored when synthesizing the UX.
const PLUG_STEP_ORDER = ['build', 'deploy'] as const;

// Map each task step to the in-process deploy-step NAME whose builder-facing
// label (`STEP_LABELS` in multi-step.ts) we want, so the queued UX reads
// identically to the streamed flow: `build` → "Building", `deploy` →
// "Deploying".
const STEP_TO_RAW: Record<string, string> = {
  build: 'cloud_build',
  deploy: 'apply_manifests',
};

/**
 * Project a task into the `Step[]` the renderer consumes. Pure + idempotent:
 * the same projection always yields the same steps, so a duplicate checkpoint
 * write (a resumed sweeper re-recording a step) never emits a duplicate line.
 *
 * Only the first not-yet-complete step is shown as active — later steps stay
 * hidden until the active one finishes, mirroring the in-process flow where
 * step rows appear as the server adds them.
 */
export function taskToSteps(p: TaskProjection): Step[] {
  const done = new Set(p.completed_steps);
  const steps: Step[] = [];
  for (const name of PLUG_STEP_ORDER) {
    const raw = STEP_TO_RAW[name];
    if (done.has(name)) {
      steps.push({ step: raw, status: 'ok' });
      continue;
    }
    if (p.status === 'success') {
      // Terminal success but the step wasn't enumerated in completed_steps —
      // treat it as done (the task is finished by definition).
      steps.push({ step: raw, status: 'ok' });
      continue;
    }
    if (p.status === 'failed') {
      // The first not-done step is where it failed (current_step is null on a
      // terminal task). Earlier steps are already 'ok' above.
      steps.push({ step: raw, status: 'failed' });
      break;
    }
    // Non-terminal: this is the active step.
    steps.push({ step: raw, status: 'in_progress' });
    break;
  }
  return steps;
}

// ─────────────────────────────────────────────────────────────────────────
// Implementation
// ─────────────────────────────────────────────────────────────────────────

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Poll `GET /api/v2/tasks/<taskId>` until terminal, driving `uxRenderer` with
 * the synthesized step list on every successful poll. Returns the terminal
 * outcome on success (or server-side cleanup); throws on failure / timeout /
 * network loss / a real not-found.
 */
export async function pollPlugTask(
  taskId: string,
  client: IV2Client,
  uxRenderer: PlugUxRenderer,
  opts: PollPlugTaskOpts = {},
): Promise<PlugTaskResult> {
  const intervalMs = opts.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_MAX_DURATION_MS;
  const sleep = opts.sleep ?? defaultSleep;
  const now = opts.now ?? Date.now;
  const slug = opts.slug ?? taskId;
  const traceId = opts.traceId ?? taskId;
  const path = `/api/v2/tasks/${encodeURIComponent(taskId)}`;

  const startedAt = now();
  let seenOk = false;
  let netFailures = 0;
  let backoffSpentMs = 0;
  // BL595 — latched so the friendly maintenance line prints at most once even if
  // the window spans many polls.
  let maintenanceAnnounced = false;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (now() - startedAt > timeoutMs) {
      // BL544 soft-timeout parity: synthesize a `building`-status trace so the
      // shared `PollTimeoutError` classifies this SOFT (exit 2 + "taking longer
      // than expected — run grid status"). The queued task is durable and
      // continues server-side; nothing is broken, the CLI just ran out of
      // patience. Reuses the exact BL544 wording + exit-code wiring without
      // touching its timing.
      throw new PollTimeoutError(traceId, timeoutMs, {
        status: 'building',
        entity: { name: slug },
        steps: [],
      } as unknown as DeployTrace);
    }

    let proj: TaskProjection;
    try {
      proj = await client.get<TaskProjection>(path);
    } catch (err) {
      if (err instanceof V2ApiError) {
        if (err.status === 404) {
          // GC'd. If we'd already seen progress, the deploy completed and the
          // server reaped the row → graceful exit. Otherwise it's a real
          // "no such task" (stale id, or another org's task).
          if (seenOk) return { status: 'cleaned_up', task: null };
          throw new CloudGridError(
            `Task ${taskId} not found. It may have expired, or it belongs to a different grid.`,
          );
        }
        throw err; // other 4xx/5xx — a real server response, surface it.
      }
      // Transport blip — exponential backoff, abandon past the cumulative budget.
      const wait = Math.min(BACKOFF_BASE_MS * 2 ** netFailures, BACKOFF_MAX_MS);
      backoffSpentMs += wait;
      if (backoffSpentMs > NETWORK_BUDGET_MS) {
        throw new PlugTaskNetworkLostError(taskId, netFailures + 1);
      }
      netFailures += 1;
      await sleep(wait);
      continue;
    }

    // Successful poll — reset the transport-blip budget.
    netFailures = 0;
    backoffSpentMs = 0;
    seenOk = true;

    uxRenderer.update(taskToSteps(proj));

    // BL595 — surface a maintenance window: announce once, then slow the cadence.
    const maintenance =
      proj.maintenance && proj.maintenance.code === 'PLUG_MAINTENANCE'
        ? proj.maintenance
        : null;
    if (maintenance && !maintenanceAnnounced) {
      maintenanceAnnounced = true;
      opts.onMaintenance?.(maintenance);
    }

    if (proj.status === 'success') return { status: 'success', task: proj };
    if (proj.status === 'failed') {
      // BL591 — prefer the structured `error_message` (build / rollout /
      // validation / provision): render a human user-facing line per the BL580
      // vocabulary and attach the reason so the BL593 top-level catch forwards
      // `error_code` + `log_excerpt` into the cli_events row. Fall back to the
      // raw `error` string for a legacy (pre-BL591) task that carried none.
      const reason = proj.error_message ?? null;
      const message = reason
        ? formatTaskFailure(reason)
        : proj.error && proj.error.length > 0
          ? proj.error
          : `Plug task ${taskId} failed.`;
      throw new PlugTaskFailedError(taskId, message, reason ?? undefined);
    }

    // pending / running / interrupted → keep polling. `interrupted` is a
    // graceful mid-flight stop the sweeper re-claims immediately; it is
    // transient, not terminal, so we wait it out under the same poll budget.
    // BL595 — slow to the maintenance cadence while the window is open.
    await sleep(maintenance ? MAINTENANCE_POLL_INTERVAL_MS : intervalMs);
  }
}
